import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Fab,
  Zoom,
  CircularProgress,
  Chip,
  Avatar,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';



// Styled components with premium 10x design
const ChatWidget = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  width: 420,
  height: 680,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 32px)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1300,
  borderRadius: '32px',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(145deg, rgba(8, 8, 16, 0.97) 0%, rgba(18, 18, 30, 0.95) 25%, rgba(15, 15, 25, 0.97) 75%, rgba(10, 10, 20, 0.98) 100%)' 
    : 'linear-gradient(145deg, rgba(255, 255, 255, 0.97) 0%, rgba(252, 252, 255, 0.95) 25%, rgba(248, 250, 255, 0.97) 75%, rgba(255, 255, 255, 0.98) 100%)',
  backdropFilter: 'blur(60px) saturate(180%)',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(99, 102, 241, 0.25)' 
    : '2px solid rgba(99, 102, 241, 0.15)',
  boxShadow: theme.palette.mode === 'dark'
    ? `0 40px 100px rgba(0, 0, 0, 0.8),
       0 20px 60px rgba(99, 102, 241, 0.2),
       0 8px 32px rgba(139, 92, 246, 0.15),
       inset 0 1px 0 rgba(255, 255, 255, 0.1),
       inset 0 -1px 0 rgba(99, 102, 241, 0.1)`
    : `0 40px 100px rgba(99, 102, 241, 0.25),
       0 20px 60px rgba(0, 0, 0, 0.15),
       0 8px 32px rgba(99, 102, 241, 0.1),
       inset 0 1px 0 rgba(255, 255, 255, 0.9),
       inset 0 -1px 0 rgba(99, 102, 241, 0.05)`,
  transition: 'all 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
  transform: 'translateY(0) scale(1)',
  '&:hover': {
    transform: 'translateY(-4px) scale(1.01)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 50px 120px rgba(0, 0, 0, 0.9),
         0 25px 80px rgba(99, 102, 241, 0.3),
         0 12px 40px rgba(139, 92, 246, 0.2),
         inset 0 1px 0 rgba(255, 255, 255, 0.15),
         inset 0 -1px 0 rgba(99, 102, 241, 0.15)`
      : `0 50px 120px rgba(99, 102, 241, 0.3),
         0 25px 80px rgba(0, 0, 0, 0.2),
         0 12px 40px rgba(99, 102, 241, 0.15),
         inset 0 1px 0 rgba(255, 255, 255, 1),
         inset 0 -1px 0 rgba(99, 102, 241, 0.08)`,
  },
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 16px)',
    height: 'calc(100vh - 32px)',
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    left: theme.spacing(1),
    borderRadius: '28px',
  }
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 25%, #8b5cf6 50%, #a855f7 75%, #c084fc 100%)',
  color: 'white',
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  backdropFilter: 'blur(20px)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.15) 100%)',
    mask: 'linear-gradient(135deg, transparent 0%, black 50%, transparent 100%)',
    animation: 'shimmer 4s ease-in-out infinite',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
    animation: 'glow 2s ease-in-out infinite alternate',
  },
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-120%) skewX(-15deg)' },
    '100%': { transform: 'translateX(120%) skewX(-15deg)' }
  },
  '@keyframes glow': {
    '0%': { 
      opacity: 0.4,
      boxShadow: '0 0 10px rgba(255,255,255,0.2)'
    },
    '100%': { 
      opacity: 0.8,
      boxShadow: '0 0 20px rgba(255,255,255,0.4)'
    }
  }
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3, 2.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(180deg, rgba(12, 12, 20, 0.4) 0%, rgba(18, 18, 28, 0.6) 50%, rgba(15, 15, 25, 0.5) 100%)' 
    : 'linear-gradient(180deg, rgba(250, 250, 255, 0.7) 0%, rgba(245, 248, 255, 0.8) 50%, rgba(248, 250, 255, 0.7) 100%)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '20px',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, rgba(99, 102, 241, 0.05) 0%, transparent 100%)'
      : 'linear-gradient(180deg, rgba(99, 102, 241, 0.03) 0%, transparent 100%)',
    pointerEvents: 'none',
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%)'
      : 'linear-gradient(180deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%)',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, rgba(99, 102, 241, 0.4) 0%, rgba(139, 92, 246, 0.3) 100%)'
      : 'linear-gradient(180deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%)',
  },
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})(({ theme, isUser }) => ({
  maxWidth: '82%',
  padding: theme.spacing(2, 2.5),
  borderRadius: isUser ? '24px 24px 8px 24px' : '24px 24px 24px 8px',
  background: isUser 
    ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 30%, #8b5cf6 70%, #a855f7 100%)'
    : theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, rgba(30, 30, 40, 0.95) 0%, rgba(40, 40, 55, 0.90) 50%, rgba(35, 35, 50, 0.95) 100%)' 
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 255, 0.90) 50%, rgba(248, 252, 255, 0.95) 100%)',
  color: isUser ? '#ffffff' : theme.palette.text.primary,
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  position: 'relative',
  backdropFilter: 'blur(20px)',
  border: isUser 
    ? 'none'
    : theme.palette.mode === 'dark' 
      ? '1px solid rgba(99, 102, 241, 0.15)' 
      : '1px solid rgba(99, 102, 241, 0.08)',
  boxShadow: isUser
    ? `0 8px 32px rgba(99, 102, 241, 0.3),
       0 4px 16px rgba(139, 92, 246, 0.2),
       inset 0 1px 0 rgba(255, 255, 255, 0.2)`
    : theme.palette.mode === 'dark'
      ? `0 8px 32px rgba(0, 0, 0, 0.4),
         0 4px 16px rgba(99, 102, 241, 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.05)`
      : `0 8px 32px rgba(99, 102, 241, 0.08),
         0 4px 16px rgba(0, 0, 0, 0.05),
         inset 0 1px 0 rgba(255, 255, 255, 0.9)`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'translateY(0)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: isUser
      ? `0 12px 40px rgba(99, 102, 241, 0.4),
         0 6px 20px rgba(139, 92, 246, 0.3),
         inset 0 1px 0 rgba(255, 255, 255, 0.3)`
      : theme.palette.mode === 'dark'
        ? `0 12px 40px rgba(0, 0, 0, 0.5),
           0 6px 20px rgba(99, 102, 241, 0.15),
           inset 0 1px 0 rgba(255, 255, 255, 0.08)`
        : `0 12px 40px rgba(99, 102, 241, 0.12),
           0 6px 20px rgba(0, 0, 0, 0.08),
           inset 0 1px 0 rgba(255, 255, 255, 1)`,
  },
  '&::before': isUser ? {} : {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '24px 24px 24px 8px',
    padding: '1px',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'xor',
    WebkitMaskComposite: 'xor',
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, rgba(15, 15, 25, 0.95) 0%, rgba(25, 25, 40, 0.90) 50%, rgba(20, 20, 35, 0.95) 100%)' 
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 252, 255, 0.90) 50%, rgba(248, 250, 255, 0.95) 100%)',
  borderTop: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-end',
  backdropFilter: 'blur(30px)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.3), transparent)',
  }
}));

const FloatingButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1300,
  background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 25%, #8b5cf6 50%, #a855f7 75%, #c084fc 100%)',
  width: 72,
  height: 72,
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(99, 102, 241, 0.2)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? `0 16px 48px rgba(99, 102, 241, 0.5),
       0 8px 24px rgba(139, 92, 246, 0.3),
       0 4px 12px rgba(168, 85, 247, 0.2),
       inset 0 1px 0 rgba(255, 255, 255, 0.1)`
    : `0 16px 48px rgba(99, 102, 241, 0.4),
       0 8px 24px rgba(139, 92, 246, 0.2),
       0 4px 12px rgba(168, 85, 247, 0.15),
       inset 0 1px 0 rgba(255, 255, 255, 0.8)`,
  backdropFilter: 'blur(20px)',
  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
  transform: 'translateY(0) scale(1) rotate(0deg)',
  animation: 'pulse 2s ease-in-out infinite',
  '&:hover': {
    background: 'linear-gradient(135deg, #3730a3 0%, #4f46e5 25%, #7c3aed 50%, #9333ea 75%, #a855f7 100%)',
    transform: 'translateY(-4px) scale(1.08) rotate(5deg)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 24px 64px rgba(99, 102, 241, 0.6),
         0 12px 32px rgba(139, 92, 246, 0.4),
         0 6px 16px rgba(168, 85, 247, 0.3),
         inset 0 1px 0 rgba(255, 255, 255, 0.15)`
      : `0 24px 64px rgba(99, 102, 241, 0.5),
         0 12px 32px rgba(139, 92, 246, 0.3),
         0 6px 16px rgba(168, 85, 247, 0.2),
         inset 0 1px 0 rgba(255, 255, 255, 1)`,
  },
  '&:active': {
    transform: 'translateY(-2px) scale(1.04) rotate(2deg)',
  },
  '@keyframes pulse': {
    '0%, 100%': { boxShadow: theme.palette.mode === 'dark' 
      ? `0 16px 48px rgba(99, 102, 241, 0.5), 0 8px 24px rgba(139, 92, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)` 
      : `0 16px 48px rgba(99, 102, 241, 0.4), 0 8px 24px rgba(139, 92, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)` },
    '50%': { boxShadow: theme.palette.mode === 'dark' 
      ? `0 20px 60px rgba(99, 102, 241, 0.6), 0 10px 30px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)` 
      : `0 20px 60px rgba(99, 102, 241, 0.5), 0 10px 30px rgba(139, 92, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.9)` }
  },
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    width: 64,
    height: 64,
  }
}));

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [waitingForEmail, setWaitingForEmail] = useState(false);
  const messagesEndRef = useRef(null);

  // Generate session ID on first open
  useEffect(() => {
    if (isOpen && !sessionId) {
      setSessionId(Date.now().toString() + Math.random().toString(36).substr(2, 9));
    }
  }, [isOpen, sessionId]);

  // Show notification when component mounts (user lands on page)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotification(true);
    }, 2000); // Show after 2 seconds

    // Auto-hide after 10 seconds
    const hideTimer = setTimeout(() => {
      setShowNotification(false);
    }, 12000); // 2s delay + 10s visible

    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load welcome message when opened and poll for new messages
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const browserInfo = getBrowserInfo();
      
      let welcomeMessage = "Hey there! 👋 I'm Aurie, and I'm here to help you master your interviews! 🎯";
      
      // Always recommend Chrome for best experience
      if (!browserInfo.isDesktop || !browserInfo.isChrome) {
        welcomeMessage += "\n\n💡 **Pro tip:** For the smoothest experience, use **Google Chrome** on desktop/laptop (incognito mode recommended: Ctrl+Shift+N or Cmd+Shift+N) 🖥️";
      }
      
      welcomeMessage += "\n\n**I can help you with:**\n• Interview practice and feedback 🌟\n• Technical support and troubleshooting 🔧\n• Account and subscription questions 💳\n• Platform features and guidance 🚀\n\n**Need more help?** I can connect you with our support team at **support@auralyze.ai** 📧\n\nWhat can I help you with today?";
      
      setMessages([
        {
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date().toISOString()
        }
      ]);
    }

    // Poll for new messages from admin when chat is open
    if (isOpen && sessionId) {
      const pollForMessages = async () => {
        try {
          const response = await axios.get(`${API_ENDPOINTS.CHATBOT_CONVERSATION}/${sessionId}`);
          if (response.data.messages && response.data.messages.length > messages.length) {
            // New messages available, update the chat
            setMessages(response.data.messages);
          }
        } catch (error) {
          console.log('Polling error (normal if no conversation yet):', error.message);
        }
      };

      const pollInterval = setInterval(pollForMessages, 5000); // Poll every 5 seconds
      return () => clearInterval(pollInterval);
    }
  }, [isOpen, messages.length, sessionId]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Check if user is providing email for escalation
    if (waitingForEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(userMessage)) {
        // Valid email provided
        setWaitingForEmail(false);
        
        // Add user message to chat
        const userEmailMessage = {
          role: 'user',
          content: userMessage,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userEmailMessage]);
        
        // Process escalation with email
        await processEscalationWithEmail(userMessage);
        setIsLoading(false);
        return;
      } else {
        // Invalid email format
        const userEmailMessage = {
          role: 'user',
          content: userMessage,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userEmailMessage]);
        
        const errorMessage = {
          role: 'assistant',
          content: "That doesn't look like a valid email address. Please enter a valid email address so our team can respond to you directly (e.g., yourname@example.com):",
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        return;
      }
    }

    // Check if user is responding to feedback request
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.feedbackRequest && /^[1-5]$/.test(userMessage)) {
      // Handle feedback rating
      const rating = parseInt(userMessage);
      const userFeedbackMessage = {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userFeedbackMessage]);
      
      try {
        await axios.post(API_ENDPOINTS.CHATBOT_FEEDBACK, {
          sessionId,
          rating: rating
        });
        
        const thankYouMessage = {
          role: 'assistant',
          content: rating >= 4 ? 
            `Awesome, thanks! 🌟 I'm glad I could help. What else can I assist you with?` :
            `Thanks for the feedback! 💭 I'm always working to improve. What else can I help you with today?`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, thankYouMessage]);
      } catch (error) {
        console.error('Error submitting feedback:', error);
      }
      
      setIsLoading(false);
      return;
    }

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const response = await axios.post(API_ENDPOINTS.CHATBOT_MESSAGE, {
        message: userMessage,
        sessionId
      });

      const botMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: response.data.timestamp,
        escalated: response.data.escalated
      };

      setMessages(prev => [...prev, botMessage]);

      // Show feedback prompt after a few exchanges
      if (messages.length >= 6 && !showFeedback) {
        setTimeout(() => {
          const feedbackMessage = {
            role: 'assistant',
            content: "How did I do? 😊 Rate your experience:\n\n⭐⭐⭐⭐⭐ Excellent!\n⭐⭐⭐⭐ Good  \n⭐⭐⭐ Okay\n⭐⭐ Could be better\n⭐ Needs improvement\n\nJust type a number (1-5) to let me know! ✨",
            timestamp: new Date().toISOString(),
            feedbackRequest: true
          };
          setMessages(prev => [...prev, feedbackMessage]);
          setShowFeedback(true);
        }, 2000);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      const browserInfo = getBrowserInfo();
      let errorContent = "I'm sorry, I encountered an error. Our team has been notified and will get back to you soon!";
      
      if (!browserInfo.isDesktop || !browserInfo.isChrome) {
        errorContent += "\n\n💡 **This might help:** Try using **Google Chrome** on a laptop/desktop (incognito mode recommended: Ctrl+Shift+N or Cmd+Shift+N) for the best experience!\n\n📧 Or email **support@auralyze.ai** directly.";
      } else {
        errorContent += "\n\n💡 **Try this:** Open **Google Chrome** in incognito mode (Ctrl+Shift+N or Cmd+Shift+N)\n\n📧 Or email **support@auralyze.ai** directly.";
      }
      
      const errorMessage = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
        escalated: true,
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isDesktop = !isMobile;
    
    let browserName = 'Unknown';
    if (userAgent.indexOf('Chrome') > -1) browserName = 'Chrome';
    else if (userAgent.indexOf('Safari') > -1) browserName = 'Safari';
    else if (userAgent.indexOf('Firefox') > -1) browserName = 'Firefox';
    else if (userAgent.indexOf('Edge') > -1) browserName = 'Edge';
    
    return { isMobile, isChrome, isDesktop, browserName, userAgent };
  };

  const processEscalationWithEmail = async (userEmail) => {
    try {
      // Send escalation request to backend with user email and conversation history
      const response = await axios.post(API_ENDPOINTS.CHATBOT_ESCALATE, { 
        sessionId,
        userEmail,
        conversationHistory: messages,
        reason: 'user_request_with_email'
      });
      
      const escalationMessage = {
        role: 'assistant',
        content: `Perfect! I've sent your request directly to our support team. 📧

✅ **Angelina, Faris, and our support team have been automatically notified**
✅ **They have your email: ${userEmail}**
✅ **They have your full conversation history**

Our team will respond to you directly at: **${userEmail}** within a few hours!

No need to do anything else - just wait for their email response. 🌟`,
        timestamp: new Date().toISOString(),
        escalated: true
      };
      setMessages(prev => [...prev, escalationMessage]);
      
    } catch (error) {
      console.error('Error processing escalation:', error);
      const errorMessage = {
        role: 'assistant',
        content: `I'm sorry, I encountered an issue sending your request automatically. Please email our team directly at:

📧 **angelina.aziz@auralyze.ai**
📧 **faris.elsayad@auralyze.ai**
📧 **support@auralyze.ai**

Please mention:
- Session ID: ${sessionId}
- Your email: ${userEmail}

They'll help you right away!`,
        timestamp: new Date().toISOString(),
        escalated: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleEscalate = async () => {
    try {
      if (!sessionId) {
        console.error('No session ID available for escalation');
        return;
      }
      
      // Ask for user's email first
      const emailRequestMessage = {
        role: 'assistant',
        content: "I'd be happy to escalate your request to our human support team! 👥\n\nTo ensure they can respond to you directly, please provide your email address:",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, emailRequestMessage]);
      setWaitingForEmail(true);
    } catch (error) {
      console.error('Error escalating:', error);
      
      const browserInfo = getBrowserInfo();
      
      let troubleshootingMessage = "I'm sorry, I encountered an issue with the escalation system. Please contact our team directly at angelina.aziz@auralyze.ai or faris.elsayad@auralyze.ai. ";
      
      if (!browserInfo.isDesktop || !browserInfo.isChrome) {
        troubleshootingMessage += `\n\n🔧 **For the best experience, please:**\n`;
        
        if (!browserInfo.isDesktop) {
          troubleshootingMessage += `• Switch to a **laptop or desktop computer**\n`;
        }
        
        if (!browserInfo.isChrome) {
          troubleshootingMessage += `• Use **Google Chrome** browser (you're currently using ${browserInfo.browserName})\n`;
        }
        
        troubleshootingMessage += `• Open **Google Chrome** in incognito mode (Ctrl+Shift+N or Cmd+Shift+N)\n\nThis will ensure all features work properly! You can also email us directly at **support@auralyze.ai**`;
      } else {
        troubleshootingMessage += "Please try again in **Google Chrome** incognito mode or send us an email directly at **support@auralyze.ai**";
      }
      
      const errorMessage = {
        role: 'assistant',
        content: troubleshootingMessage,
        timestamp: new Date().toISOString(),
        escalated: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };



  const formatMessage = (content) => {
    // Simple markdown-style formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');
  };

  if (!isOpen) {
    return (
      <Zoom in={true}>
        <FloatingButton
          onClick={() => setIsOpen(true)}
          aria-label="Open AI Chat"
        >
          <ChatIcon />
        </FloatingButton>
      </Zoom>
    );
  }

  return (
    <>
      <Zoom in={isOpen}>
        <ChatWidget elevation={8}>
          {/* Header */}
          <ChatHeader>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{ 
                  width: 42, 
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  boxShadow: `0 8px 32px rgba(255, 255, 255, 0.1),
                              inset 0 1px 0 rgba(255, 255, 255, 0.4)`,
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem'
                }}
              >
                🌟
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  Aurie ✨
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: '0.8rem',
                    textShadow: '0 1px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  Your interview guide 🎯
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setIsOpen(false)}
              sx={{ 
                color: 'white',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                width: 36,
                height: 36,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 16px rgba(255,255,255,0.2)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </ChatHeader>

          {/* Messages */}
          <MessagesContainer>
            {messages.map((message, index) => (
              <Box 
                key={index} 
                display="flex" 
                alignItems="flex-end" 
                gap={1.5}
                flexDirection={message.role === 'user' ? 'row-reverse' : 'row'}
              >
                <Avatar 
                  sx={{ 
                    background: message.role === 'assistant' 
                      ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 30%, #8b5cf6 70%, #a855f7 100%)' 
                      : 'linear-gradient(135deg, #475569 0%, #64748b 50%, #94a3b8 100%)', 
                    width: 36, 
                    height: 36,
                    border: message.role === 'assistant'
                      ? '2px solid rgba(99, 102, 241, 0.3)'
                      : '2px solid rgba(100, 116, 139, 0.3)',
                    boxShadow: message.role === 'assistant'
                      ? `0 8px 32px rgba(99, 102, 241, 0.25),
                         0 4px 16px rgba(139, 92, 246, 0.15),
                         inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                      : `0 8px 32px rgba(100, 116, 139, 0.25),
                         0 4px 16px rgba(71, 85, 105, 0.15),
                         inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: message.role === 'assistant'
                        ? `0 12px 40px rgba(99, 102, 241, 0.3),
                           0 6px 20px rgba(139, 92, 246, 0.2),
                           inset 0 1px 0 rgba(255, 255, 255, 0.3)`
                        : `0 12px 40px rgba(100, 116, 139, 0.3),
                           0 6px 20px rgba(71, 85, 105, 0.2),
                           inset 0 1px 0 rgba(255, 255, 255, 0.3)`
                    }
                  }}
                >
                  <Typography sx={{ fontSize: '1.1rem' }}>
                    {message.role === 'assistant' ? '🌟' : '👤'}
                  </Typography>
                </Avatar>
                <MessageBubble isUser={message.role === 'user'}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ 
                        lineHeight: 1.5,
                        fontSize: '0.9rem'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatMessage(message.content)
                      }}
                    />
                    {message.escalated && (
                      <Chip
                        icon={<WarningIcon />}
                        label="Escalated to team"
                        size="small"
                        sx={{ 
                          mt: 1.5,
                          height: 28,
                          fontSize: '0.7rem',
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.9) 100%)',
                          color: 'white',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          boxShadow: '0 2px 8px rgba(34, 197, 94, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          '& .MuiChip-icon': {
                            color: 'white',
                            fontSize: '0.8rem'
                          }
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.7rem',
                        textAlign: message.role === 'user' ? 'right' : 'left'
                      }}
                    >
                      {new Date(message.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </MessageBubble>
              </Box>
            ))}
            
            {isLoading && (
              <Box 
                display="flex" 
                alignItems="flex-end" 
                gap={1.5}
                flexDirection="row"
              >
                <Avatar 
                  sx={{ 
                    bgcolor: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
                    width: 32, 
                    height: 32,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                >
                  <BotIcon fontSize="small" />
                </Avatar>
                <MessageBubble isUser={false}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CircularProgress 
                      size={18} 
                      sx={{ 
                        color: (theme) => theme.palette.mode === 'dark' ? '#8b5cf6' : '#6366f1'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontStyle: 'italic',
                        fontSize: '0.9rem'
                      }}
                    >
                      Thinking...
                    </Typography>
                  </Box>
                </MessageBubble>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          {/* Input */}
          <InputContainer>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask me anything about Auralyze..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              size="medium"
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(45, 45, 50, 0.8)' 
                    : 'rgba(255, 255, 255, 0.9)',
                  border: (theme) => theme.palette.mode === 'dark' 
                    ? '1px solid rgba(255,255,255,0.15)' 
                    : '1px solid rgba(0,0,0,0.1)',
                  '&:hover': {
                    border: (theme) => theme.palette.mode === 'dark' 
                      ? '1px solid rgba(139, 92, 246, 0.5)' 
                      : '1px solid rgba(99, 102, 241, 0.3)',
                  },
                  '&.Mui-focused': {
                    border: (theme) => theme.palette.mode === 'dark' 
                      ? '1px solid rgba(139, 92, 246, 0.8)' 
                      : '1px solid rgba(99, 102, 241, 0.6)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' 
                      ? '0 0 0 3px rgba(139, 92, 246, 0.15)' 
                      : '0 0 0 3px rgba(99, 102, 241, 0.1)',
                  }
                },
                '& .MuiOutlinedInput-input': {
                  color: (theme) => theme.palette.text.primary,
                  fontSize: '0.9rem',
                  '&::placeholder': {
                    color: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.5)' 
                      : 'rgba(0,0,0,0.5)',
                    opacity: 1,
                  }
                }
              }}
            />
            <IconButton
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              sx={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                width: 48,
                height: 48,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  background: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.1)' 
                    : 'rgba(0,0,0,0.1)',
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.3)' 
                    : 'rgba(0,0,0,0.3)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <SendIcon />
            </IconButton>
          </InputContainer>

          {/* Quick Actions */}
          <Box p={2} display="flex" gap={1} flexWrap="wrap" justifyContent="center">
            <Chip
              label="How to practice interviews?"
              size="medium"
              onClick={() => setInputValue("How can I practice video interviews on Auralyze?")}
              sx={{ 
                fontSize: '0.8rem',
                borderRadius: '16px',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(168, 85, 247, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 50%, rgba(168, 85, 247, 0.08) 100%)',
                color: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(192, 132, 252, 0.9)'
                  : 'rgba(99, 102, 241, 0.9)',
                border: (theme) => theme.palette.mode === 'dark' 
                  ? '1px solid rgba(99, 102, 241, 0.2)' 
                  : '1px solid rgba(99, 102, 241, 0.15)',
                backdropFilter: 'blur(10px)',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                  : '0 2px 8px rgba(99, 102, 241, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.25) 50%, rgba(168, 85, 247, 0.25) 100%)'
                    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(168, 85, 247, 0.15) 100%)',
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(192, 132, 252, 1)'
                    : 'rgba(99, 102, 241, 1)',
                  border: (theme) => theme.palette.mode === 'dark' 
                    ? '1px solid rgba(99, 102, 241, 0.4)' 
                    : '1px solid rgba(99, 102, 241, 0.3)',
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 4px 16px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                    : '0 4px 16px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
                },
                '&:active': {
                  transform: 'translateY(0) scale(1.01)',
                }
              }}
            />
            <Chip
              label="Need human help? (wait for AI first)"
              size="medium"
              onClick={handleEscalate}
              sx={{ 
                fontSize: '0.75rem',
                borderRadius: '16px',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.9) 0%, rgba(109, 40, 217, 0.9) 50%, rgba(91, 33, 182, 0.9) 100%)'
                  : 'linear-gradient(135deg, rgba(124, 58, 237, 0.85) 0%, rgba(109, 40, 217, 0.85) 50%, rgba(91, 33, 182, 0.85) 100%)',
                color: 'white',
                border: (theme) => theme.palette.mode === 'dark' 
                  ? '1px solid rgba(124, 58, 237, 0.3)'
                  : '1px solid rgba(124, 58, 237, 0.2)',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0 4px 16px rgba(124, 58, 237, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : '0 4px 16px rgba(124, 58, 237, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(109, 40, 217, 0.95) 0%, rgba(91, 33, 182, 0.95) 50%, rgba(76, 29, 149, 0.95) 100%)'
                      : 'linear-gradient(135deg, rgba(109, 40, 217, 0.9) 0%, rgba(91, 33, 182, 0.9) 50%, rgba(76, 29, 149, 0.9) 100%)',
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                      ? '0 8px 24px rgba(124, 58, 237, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                      : '0 8px 24px rgba(124, 58, 237, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                  },
                '&:active': {
                  transform: 'translateY(-1px) scale(1.01)',
                }
              }}
            />
          </Box>
        </ChatWidget>
      </Zoom>

      {/* Homepage notification */}
      <Snackbar
        open={showNotification && !isOpen}
        autoHideDuration={10000}
        onClose={() => setShowNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ 
          bottom: '90px !important',
          right: '20px !important'
        }}
      >
        <Alert 
          onClose={() => setShowNotification(false)} 
          severity="info"
          sx={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 30%, #8b5cf6 70%, #a855f7 100%)',
            color: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(20px)',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            👋 Hi! I'm Aurie, your AI interview assistant. Click the chat button if you need help! ✨
          </Typography>
        </Alert>
      </Snackbar>

    </>
  );
};

export default AIChatbot; 